// src/hooks/useSubscription.js
import { useState, useEffect } from 'react';
import useAuthToken from './useAuthToken';
import apiClient from '../api/client';
import { notifySubscriptionError } from './useSubscriptionNotifications';

const useSubscription = () => {
  const [subscription, setSubscription] = useState({
    loading: true,
    data: null,
    error: null
  });
  
  const token = useAuthToken();

  useEffect(() => {
    const fetchSubscription = async () => {
      // Skip subscription checks on admin routes
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      if (isAdminRoute) {
        setSubscription(prev => ({ ...prev, loading: false }));
        return;
      }

      if (!token) {
        setSubscription(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const response = await apiClient.get('/billing/subscription', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = response.data;
        
        setSubscription({
          loading: false,
          data,
          error: null
        });

      } catch (error) {
        console.warn('Failed to fetch subscription:', error);
        
        // Notify subscription notification system of the error
        notifySubscriptionError(error);
        
        setSubscription({
          loading: false,
          data: null,
          error: error.response?.data?.detail || error.message
        });
      }
    };

    fetchSubscription();
  }, [token]);

  const cancelSubscription = async (reason, feedback = '', cancelImmediately = false) => {
    if (!token) throw new Error('Authentication required');

    try {
      const response = await apiClient.post('/billing/cancel-subscription', {
        reason,
        feedback,
        cancel_immediately: cancelImmediately
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = response.data;
    
      // Refresh subscription data
      setSubscription(prev => ({
        ...prev,
        data: {
          ...prev.data,
          ...result.subscription
        }
      }));

      return result;
    } catch (error) {
      // Notify subscription notification system of the error
      notifySubscriptionError(error);
      
      throw new Error(error.response?.data?.detail || error.message || 'Failed to cancel subscription');
    }
  };

  const reactivateSubscription = async () => {
    if (!token) throw new Error('Authentication required');

    try {
      const response = await apiClient.post('/billing/reactivate-subscription', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const result = response.data;
      
      // Refresh subscription data
      setSubscription(prev => ({
        ...prev,
        data: {
          ...prev.data,
          ...result.subscription
        }
      }));

      return result;
    } catch (error) {
      // Notify subscription notification system of the error
      notifySubscriptionError(error);
      
      throw new Error(error.response?.data?.detail || error.message || 'Failed to reactivate subscription');
    }
  };

  // Add retry function for failed requests
  const retryFetch = async () => {
    setSubscription(prev => ({ 
      ...prev, 
      loading: true, 
      error: null, 
      retryCount: prev.retryCount + 1 
    }));
    
    // Re-trigger useEffect by updating token dependency
    const currentToken = localStorage.getItem('auth_token');
    if (currentToken && currentToken === token) {
      // Force re-fetch by setting a small delay
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  const subscriptionAPI = {
    subscription: subscription.data,
    loading: subscription.loading,
    error: subscription.error,
    retryCount: subscription.retryCount,
    lastFetch: subscription.lastFetch,
    retryFetch,
    cancelSubscription,
    reactivateSubscription,
    // Helper functions
    isActive: subscription.data?.status === 'active' && !subscription.data?.cancel_at_period_end,
    isCanceled: subscription.data?.status === 'canceled' || subscription.data?.cancel_at_period_end,
    isTrialing: subscription.data?.status === 'trialing',
    canCancel: ['active', 'trialing'].includes(subscription.data?.status) && !subscription.data?.cancel_at_period_end,
    canReactivate: (subscription.data?.status === 'canceled' && subscription.data?.can_reactivate) || subscription.data?.cancel_at_period_end
  };

  // Add automatic retry logic for temporary failures
  useEffect(() => {
    if (subscription.error && subscription.retryCount < 3) {
      const shouldAutoRetry = (
        subscription.error.includes('temporarily unavailable') ||
        subscription.error.includes('network') ||
        subscription.error.includes('timeout') ||
        subscription.error.includes('connect')
      );
      
      if (shouldAutoRetry) {
        const retryDelay = Math.min(1000 * Math.pow(2, subscription.retryCount), 10000); // Exponential backoff, max 10s
        console.log(`Auto-retrying subscription fetch in ${retryDelay}ms (attempt ${subscription.retryCount + 1}/3)`);
        
        const retryTimer = setTimeout(() => {
          retryFetch();
        }, retryDelay);
        
        return () => clearTimeout(retryTimer);
      }
    }
  }, [subscription.error, subscription.retryCount]);
  
  return subscriptionAPI;
};

export default useSubscription;